// GM Print Blog — "Finding the hours back" series by Sarah D'Ardenne.
// To add a new post: append to POSTS array. Each post needs slug, title, excerpt,
// author, date, readTime, image, and body (array of strings, {heading, body} objects,
// or {list: [...]} objects for bullet lists).

const SARAH = "Sarah D'Ardenne";
const ROLE = "Brand & Marketing Director";
const SERIES = "Finding the Hours Back";

export const POSTS = [
  {
    slug: "owners-do-not-have-a-time-problem",
    title: "Owners do not have a time problem",
    excerpt: `It's tempting to think a busy owner needs more hours. Most don't. What they need is a way to handle the repeatable parts of the business, so their attention goes to the work only they can do.`,
    author: SARAH,
    authorRole: ROLE,
    category: SERIES,
    date: "2026-10-14",
    readTime: "2 min read",
    image: null,
    body: [
      `It is tempting to think a busy owner needs more hours. Most do not. What they need is a way to handle the repeatable parts of the business, so that their attention goes to the work only they can do.`,
      `The 2026 Chamber and Ipsos survey points to what is holding many businesses back. Privacy and security concerns came up often, as did uncertainty about how AI fits the business and a skills gap. Very few small business workers said they had been offered formal AI training. The tools are available. What is often missing is clarity about where they fit and time to learn them.`,
      `Three questions can help you work out where to start:`,
      {
        list: [
          `Which task do we repeat most often?`,
          `Who checks the output before a customer sees it?`,
          `What would we do with an hour a week, and is that hour already in the calendar?`,
        ],
      },
      `None of this requires a large budget or a new hire. It requires a clear decision about what to automate, a person who reviews the result, and a plan for the time it gives back. Owners who get those three things right tend to find that the business runs on a system rather than on memory and goodwill.`,
      `Which part of your business still runs on your memory, and what would happen to it if you were away for two weeks?`,
    ],
  },
  {
    slug: "the-hours-need-a-job",
    title: "The hours need a job",
    excerpt: `Suppose you do find ten hours a week. What happens to them? For many people the answer is more admin. If growth is the goal, the choice is worth making on purpose.`,
    author: SARAH,
    authorRole: ROLE,
    category: SERIES,
    date: "2026-10-07",
    readTime: "2 min read",
    image: null,
    body: [
      `Suppose you do find ten hours a week. What happens to them? For many people the answer is more admin. The inbox fills up again, a new task appears, and the week feels just as full.`,
      `The 2026 Chamber and Ipsos survey shows where people tend to put the time they save. Most of those who saved time put it towards more or higher-quality work, or towards learning and planning. But a meaningful share used it to avoid overtime, or for breaks and personal tasks. None of those choices is wrong. If growth is the goal, though, the choice is worth making on purpose.`,
      `Before you free up any time, decide where it goes. Pick one or two of these:`,
      {
        list: [
          `Follow up every open quote within 48 hours.`,
          `Call back past customers once a quarter.`,
          `Answer every new enquiry within an hour.`,
          `Ask every happy customer for a review or referral.`,
          `Spend one hour a week on visibility, such as filming or posting.`,
          `Go home on time. This counts too.`,
        ],
      },
      `Put the chosen work in your calendar before you automate anything. A block that is already booked tends to survive the week, and an empty afternoon tends to fill up with whatever is loudest.`,
      `If you got five hours back next week, what would you do with them, and would you be able to say so by Friday?`,
    ],
  },
  {
    slug: "the-quotes-that-quietly-go-cold",
    title: "The quotes that quietly go cold",
    excerpt: `Most owners don't lose work because they are bad at selling. They lose it because follow-up happens when someone remembers — and memory is not a system.`,
    author: SARAH,
    authorRole: ROLE,
    category: SERIES,
    date: "2026-09-30",
    readTime: "2 min read",
    image: null,
    body: [
      `Most owners do not lose work because they are bad at selling. They lose it because follow-up happens when someone remembers, and memory is not a system. A quote that gets no reply is rarely rejected. More often, it was simply forgotten.`,
      `Microsoft Research and Harvard Business School found that giving workers AI tools cut the time they spent in email and gave them more time away from it. The study also found no change in the mix of tasks people did. Saving time does not decide what happens next. Someone still has to decide that a quote gets a check-in on day two, a likely question gets an answer on day five, and a polite last note goes out on day ten.`,
      `A simple sequence might look like this. On day two, a friendly check-in. On day five, a reply to the most common hesitation. On day ten, a short, polite close with an easy way to say no. Each message should be brief and sound like a real person wrote it, because one did.`,
      `Once the sequence exists, it can be drafted in advance and reviewed in a few minutes, rather than relying on anyone's memory. That is the difference between a follow-up habit and a follow-up system. The study covered large firms, so the scale will differ for a small business, but the principle holds: time saved only matters if you have designed where it goes.`,
      `When did you last check how many quotes are still waiting for an answer, and who is responsible for chasing them?`,
    ],
  },
  {
    slug: "the-quote-you-have-written-fifty-times",
    title: "The quote you have written fifty times",
    excerpt: `Every business has a task like this. The same quote, the same invoice reminder, written so many times you could do it half asleep. It feels like work, because it is work. It just is not the kind that grows the business.`,
    author: SARAH,
    authorRole: ROLE,
    category: SERIES,
    date: "2026-09-23",
    readTime: "3 min read",
    image: null,
    body: [
      `Every business has a task like this. The same quote, the same invoice reminder, the same "here are our hours" email, written so many times you could do it half asleep. It feels like work, because it is work. It just is not the kind that grows the business.`,
      `In the same 2026 Chamber and Ipsos survey, most AI users said they used it for personal productivity, such as drafting and summarising. Only a small share said they used it for recurring work or to automate workflows. The recurring work is still largely done by hand, and that is where the opportunity sits.`,
      `A simple way to find your first candidate is to score each Required or Waste task from 1 to 3 on three questions:`,
      {
        list: [
          `How many hours does it take each week? (Under one hour scores 1; three or more hours scores 3.)`,
          `How often does it look the same? (Different every time scores 1; basically identical scores 3.)`,
          `Could you explain it as a checklist? (Needs judgement scores 1; yes, easily scores 3.)`,
        ],
      },
      `Anything that scores high on all three is probably the best first candidate. Start with the top three, not ten.`,
      `For a quote, the checklist might include the job type, the materials, your standard rates and the terms you always include. A first draft built from those notes is repeatable. Checking the price and sending it is judgement, and that part stays with you. Keep a person checking anything before it reaches a customer, at least for the first few weeks. The aim is a faster draft, not an unchecked one.`,
      `Which task have you written, or answered, more times than you can count, and what would it be worth to you to stop doing it by hand?`,
    ],
  },
  {
    slug: "where-did-your-tuesday-go",
    title: "Where did your Tuesday go?",
    excerpt: `Most small business owners can tell you roughly how their week went. Far fewer can tell you where the hours actually went. Those are different things, and the gap between them is usually where time gets lost.`,
    author: SARAH,
    authorRole: ROLE,
    category: SERIES,
    date: "2026-09-16",
    readTime: "3 min read",
    image: null,
    body: [
      `Most small business owners can tell you roughly how their week went. Far fewer can tell you where the hours actually went. Those are different things, and the gap between them is usually where time gets lost.`,
      `It is a common gap. A 2026 survey of 1,070 U.S. small business workers by the U.S. Chamber of Commerce Foundation and Ipsos found that half of them use AI at work, and most use it for everyday tasks like drafting, summarising and brainstorming. Plenty of owners are already using these tools. Far fewer have looked at how their own day is spent.`,
      `Try this on one ordinary workday. Every half hour, write down what you did. Keep it rough: "Inbox, six emails." "Looked for last month's supplier invoice." Voice notes count. The point is to capture the day as it happened, not as you would like it to look.`,
      `At the end of the day, mark each line with one of three labels:`,
      {
        list: [
          `Revenue: work that brings money in, such as talking to a client who is ready to buy or delivering work someone has paid for.`,
          `Required: work that has to happen but does not earn money directly, such as invoicing, scheduling, reporting and routine email.`,
          `Waste: work that should not exist, such as searching for a file you have already filed or retyping the same answer.`,
        ],
      },
      `Be honest with the labels. Re-reading an email three times is waste. Writing a quote you have written fifty times is required work, not revenue. The sale is revenue; the typing is not. Then total the hours in each group. For many owners, the Required number is the surprise.`,
      `Microsoft Research and Harvard Business School ran a field study with 7,137 knowledge workers and found that AI built into email and documents cut the time people spent in Outlook by about 1.4 hours a week. It did not change the kinds of tasks they did. Those were large firms, so your results may differ, but the lesson carries over: AI can clear some of the noise, and deciding what the freed time is for is still your call.`,
      `So here is the question worth sitting with: if someone asked you how last Tuesday was split between selling, admin and wasted effort, could you answer without guessing?`,
    ],
  },
];

export const getPostBySlug = (slug) => POSTS.find((p) => p.slug === slug);
